import React, {ReactElement, ReactNode} from 'react';
import styled from 'styled-components';

import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {
  buildNoNameNoHandlesCardDisplayName,
  getCardHandlesByType,
} from '../../../../helpers/models/cardHelpers';
import {CardModel} from '../../../../models/api/cardModel';
import {ContactSourcesEnum} from '../../../../models/api/contactModel';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {ContactCardAccessIndicator} from '../../../contacts/common/contactCardAccessIndicator';
import {AvatarInitials} from '../../avatars/avatarInitials';
import {CardAvatar} from '../../avatars/cardAvatar';
import {Skeleton} from '../../skeleton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellPropsWithAvatar, TableCellStyleProps} from './tableCell';

/*
 * Constants.
 */

const cardInitialsSize = 20; // px.

/*
 * Styles.
 */

const StyledCardCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledCardAvatarDivProps {
  $avatarSize: number;
}
const StyledCardAvatarDiv = styled.div<StyledCardAvatarDivProps>`
  flex: 0 0 ${(p) => p.$avatarSize}px;
  margin-right: 8px;
`;

const StyledCardDetailsContainerDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: row;
  align-items: center;
  overflow: hidden;
  gap: 4px;
`;

interface StyledCardNameDivProps {
  $isSelected: boolean;
}
const StyledCardNameDiv = styled.div<StyledCardNameDivProps>`
  display: block;
  align-items: center;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  overflow: hidden;

  ${(p) => getCellTextColor(p.$isSelected)}
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  ${ellipsis()};
`;

const StyledAvatarInitials = styled(AvatarInitials)`
  line-height: 24px;
`;

/*
 * Component.
 */

export const CardCell = (props: TableCellPropsWithAvatar<CardModel>): ReactElement => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    avatarSize,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);
  const localize = useLocalize();

  return (
    <TableCell<CardModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledCardCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledCardAvatarDiv $avatarSize={avatarSizePx}>
            <Skeleton
              height={`${avatarSizePx}px`}
              width={`${avatarSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
          </StyledCardAvatarDiv>
          <StyledCardDetailsContainerDiv>
            <StyledCardNameDiv $isSelected={false}>
              <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
            </StyledCardNameDiv>
          </StyledCardDetailsContainerDiv>
        </StyledCardCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledCardCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledCardAvatarDiv $avatarSize={avatarSizePx}>
            <CardAvatar
              card={value}
              size={avatarSizePx}
              renderInitials={(initials) => (
                <StyledAvatarInitials
                  size={cardInitialsSize}
                  textColor={theme.invertedText}
                  value={initials}
                />
              )}
            />
          </StyledCardAvatarDiv>
          <StyledCardDetailsContainerDiv>
            <StyledCardNameDiv $isSelected={Boolean(isSelected)}>
              {renderCardName(value, localize)}
            </StyledCardNameDiv>
            <ContactCardAccessIndicator
              card={value}
              isSelected={isSelected}
              iconSize={VisualSizesEnum.SMALL}
            />
          </StyledCardDetailsContainerDiv>
        </StyledCardCellContainerDiv>
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderCardName(card: CardModel, localize: Localizer) {
  const name = renderCardNameToShow(card, localize);
  return (
    <StyledTooltipCoordinator render={() => name}>
      <TooltipOverflowCondition>{name}</TooltipOverflowCondition>
    </StyledTooltipCoordinator>
  );
}

function renderCardNameToShow(card: CardModel, localize: Localizer): ReactNode {
  if (card.name) {
    return card.name;
  }

  // Use first email if no card name.
  const emails = getCardHandlesByType(card, ContactSourcesEnum.EMAIL);
  if (emails.length) {
    return emails[0];
  }

  // Use any handle if no email.
  const allHandles = getCardHandlesByType(card);
  if (allHandles.length) {
    return allHandles[0];
  }

  return buildNoNameNoHandlesCardDisplayName(localize);
}
